import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Loader2 } from "lucide-react";

// Eager load - critical path pages
import Index from "./pages/Index";
import Login from "./pages/Login";

// Lazy load - secondary pages (reduces initial bundle by ~65%)
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const ClientCRM = lazy(() => import("./pages/ClientCRM"));
const CRMAnalytics = lazy(() => import("./pages/CRMAnalytics"));
const Distintus = lazy(() => import("./pages/Distintus"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: 2 * 60 * 60 * 1000, // 2 hours
      gcTime: 2 * 60 * 60 * 1000, // 2 hours
      refetchInterval: false,
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin>
                  <AdminPanel />
                </ProtectedRoute>
              } />
              <Route path="/crm" element={
                <ProtectedRoute>
                  <ClientCRM />
                </ProtectedRoute>
              } />
              <Route path="/crm/analytics" element={
                <ProtectedRoute>
                  <CRMAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/crm/:slug" element={
                <ProtectedRoute>
                  <ClientCRM />
                </ProtectedRoute>
              } />
              <Route path="/distintus" element={
                <ProtectedRoute>
                  <Distintus />
                </ProtectedRoute>
              } />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
